import { Check, Pencil, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { AutoGrowTextarea } from '@/components/ui/AutoGrowTextarea'
import { Tooltip } from '@/components/ui/Tooltip'
import type { PayoutComment } from '@/lib/invoice-types'
import {
  useAddPayoutComment,
  useDeletePayoutComment,
  usePayoutComments,
  useUpdatePayoutComment,
} from '@/lib/payout-queries'
import { cn } from '@/lib/utils'

function when(iso: string): string {
  return `${iso.slice(8, 10)}-${iso.slice(5, 7)} ${iso.slice(11, 16)}`
}

/**
 * De correspondentie tussen de beheerder en de ontvanger over een uitbetaalregel.
 * De afkeurreden staat als DECLINE-reactie in dezelfde draad, zodat het gesprek en
 * de aanleiding niet uit elkaar lopen.
 *
 * Een DECLINE-reactie corrigeren schrijft door naar de afkeurreden op de regel; de
 * server bewaakt dat en weigert hem te verwijderen zolang de regel is afgekeurd.
 * Vandaar dat canEdit en canDelete van de server komen en niet uit isMine.
 */
export function PayoutCommentsPanel({ lineId, canComment }: { lineId: string; canComment: boolean }) {
  const { data: comments, isLoading } = usePayoutComments(lineId, true)
  const add = useAddPayoutComment(lineId)
  const [text, setText] = useState('')

  const submit = () => {
    const body = text.trim()
    if (!body || add.isPending) return
    add.mutate(body, { onSuccess: () => setText('') })
  }

  return (
    <div className="rounded-lg bg-fill-1 p-3 ring-1 ring-inset ring-hairline">
      {isLoading ? (
        <p className="text-xs text-ink-soft">Laden...</p>
      ) : !comments?.length ? (
        <p className="text-xs text-ink-soft">Nog geen reacties bij deze regel.</p>
      ) : (
        <ul className="space-y-2.5">
          {comments.map((c) => (
            <CommentRow key={c.id} lineId={lineId} comment={c} />
          ))}
        </ul>
      )}

      {canComment && (
        <div className="mt-3 flex items-end gap-2">
          <AutoGrowTextarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                submit()
              }
            }}
            placeholder="Schrijf een reactie..."
            className="field flex-1"
          />
          <button type="button" className="btn-secondary shrink-0" onClick={submit} disabled={!text.trim() || add.isPending}>
            Plaats
          </button>
        </div>
      )}
    </div>
  )
}

function CommentRow({ lineId, comment }: { lineId: string; comment: PayoutComment }) {
  const update = useUpdatePayoutComment(lineId)
  const remove = useDeletePayoutComment(lineId)
  const [draft, setDraft] = useState<string | null>(null)
  const editing = draft !== null
  const busy = update.isPending || remove.isPending

  const save = () => {
    const body = (draft ?? '').trim()
    if (!body || busy) return
    update.mutate({ commentId: comment.id, text: body }, { onSuccess: () => setDraft(null) })
  }

  return (
    <li
      className={cn(
        'group/comment border-l-2 pl-2.5',
        comment.kind === 'DECLINE' ? 'border-l-status-declined' : 'border-l-hairline',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 text-xs text-ink-soft">
          {comment.kind === 'DECLINE' ? 'Afgekeurd door ' : ''}
          <span className="font-medium text-ink">{comment.authorName ?? 'systeem'}</span>
          {' · '}
          {when(comment.createdAt)}
          {comment.editedAt ? <span className="text-ink-faint"> · bewerkt {when(comment.editedAt)}</span> : null}
        </p>

        {editing ? null : (
          <span className="flex shrink-0 items-center gap-0.5 transition-opacity duration-150 md:opacity-0 md:group-hover/comment:opacity-100 md:focus-within:opacity-100">
            {comment.canEdit ? (
              <button
                type="button"
                className="btn-ghost h-6 w-6 p-0 text-ink-faint"
                onClick={() => setDraft(comment.body)}
                disabled={busy}
                title="Reactie aanpassen"
                aria-label="Reactie aanpassen"
              >
                <Pencil className="h-3 w-3" />
              </button>
            ) : null}
            {comment.canDelete ? (
              <button
                type="button"
                className="btn-ghost h-6 w-6 p-0 text-ink-faint hover:text-status-overdue"
                onClick={() => remove.mutate(comment.id)}
                disabled={busy}
                title="Reactie verwijderen"
                aria-label="Reactie verwijderen"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            ) : comment.canEdit ? (
              <Tooltip content="De afkeurreden kan niet weg zolang de regel is afgekeurd. Trek eerst de afkeuring in.">
                <span className="flex h-6 w-6 cursor-help items-center justify-center text-ink-faint/40">
                  <Trash2 className="h-3 w-3" />
                </span>
              </Tooltip>
            ) : null}
          </span>
        )}
      </div>

      {editing ? (
        <div className="mt-1 flex items-end gap-1.5">
          <AutoGrowTextarea
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                save()
              }
              if (e.key === 'Escape') setDraft(null)
            }}
            className="field flex-1 py-1.5 text-sm"
          />
          <button
            type="button"
            className="btn-ghost h-8 w-8 shrink-0 p-0 text-ink-faint"
            onClick={() => setDraft(null)}
            title="Annuleren"
            aria-label="Annuleren"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="btn-primary h-8 w-8 shrink-0 p-0"
            onClick={save}
            disabled={!draft.trim() || busy}
            title="Opslaan"
            aria-label="Opslaan"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm text-ink">{comment.body}</p>
      )}
    </li>
  )
}
